import React, {useState} from "react"
import {Button, ButtonGroup, Link, LoadingButton, Modal, ModalBody, ModalFooter, ModalHeader, Typography, TextField} from "@one-thd/sui-atomic-components";
import thdCustomer from "@thd-olt-global/thd-customer";
import { unenrollFromPasswordless } from "../../../../api/custAccountApi";
import { useCustomerAccount } from "../../AccountProvider";
import { useCommonContext } from "../../CommonProvider";
import { MESSAGE, MESSAGE_TYPE } from "../../../../constants/messages";
import analytics from "../../../../utils/analytics";
import { FEATURE_SWITCHES, isFeatureSwitchEnabled, isNativeApp, postDataToEventBus, isSetPasswordInSigninWithPasswordInstead, formatPhoneNumber2 } from "../../../../utils";
import { setWebauthnNoThanksWeb } from "../../../../utils/webauthnUtils";
import { getPasswordQualifications, getPasswordStrengthScore } from "../../../../utils/validators";
import { CSSTransition } from "react-transition-group";
import PasswordRequirementsOutline from "../../../../components/common/PasswordRequirementOutline/PasswordRequirementsOutline";
import { getOtpOptions, getOTPv2 } from "../../../../api/custAccountApi";
import { updatePasswordWithOTP } from "../../../../api/custAccountApi";
import { OtpLoadingView } from "../../../../components/NewVerifyOtpModal/OtpModalViews/";
import { getErrorCode, handleApiError } from "../../../../utils/errors";
import { setUrl } from "../../../../utils/urls";
import { API_URLS } from "../../../../constants/urls";


export const unenrollmentSubmitButtonAutomationId = "passwordless-unenrollment--submit-button";
export const unenrollmentCancelButtonAutomationId = "passwordless-unenrollment--cancel-button";
export const unenrollmentLinkAutomationId = "passwordless-unenrollment--link";

const START_SET_PASSWORD_IN_SIGNIN_WITH_PASSWORD_INSTEAD_VIEW = "START_SET_PASSWORD_IN_SIGNIN_WITH_PASSWORD_INSTEAD_VIEW";
const START_VERIFY_IDENTITY_VIEW = "START_VERIFY_IDENTITY_VIEW";
const START_VERIFY_CODE_VIEW = "START_VERIFY_CODE_VIEW";

const RESEND_OTP_CODE_OPTIONS = {
  email: [
    { text: "Email me a new code", value: "email"},
    { text: "Text me a code", value: "sms"},
    { text: "Call me with a code", value: "phone"}
  ],
  sms: [
    { text: "Text me a new code", value: "sms"},
    { text: "Email me a code", value: "email"},
    { text: "Call me with a code", value: "phone"}
  ],
  phone: [
    { text: "Call me with a new code", value: "phone"},
    { text: "Email me a code", value: "email"},
    { text: "Text me a code", value: "sms"}
  ]
}

function PasswordlessUnenrollmentModal({isOpen, onUnenroll, onModalClose, isSubmitting}) {
  return (
      <Modal open={isOpen} onClose={onModalClose} size='sm'>
        <ModalHeader>
          Sign In Options
        </ModalHeader>

        <ModalBody>
        <Typography weight="regular" variant="body-base">
          Are you sure you want to sign in with a password?
        </Typography>
        </ModalBody>

        <ModalFooter>
          <ButtonGroup orientation="vertical">
        <LoadingButton type="submit" loading={isSubmitting} variant="primary" fullWidth onClick={onUnenroll} data-automation-id={unenrollmentSubmitButtonAutomationId}>
          Yes, continue
        </LoadingButton>
        <Button variant="secondary" fullWidth onClick={onModalClose} data-automation-id={unenrollmentCancelButtonAutomationId}>
          No, cancel
        </Button>
          </ButtonGroup>
        </ModalFooter>
      </Modal>
  )
}

function SetPasswordInSigninWithPasswordInstead({isOpen, onModalClose, email, setStartVerifyIdentityView, password, setPassword}) {

  const [showPass, setShowPass] = useState(false);
  const [isNewPasswordFocus, setIsNewPasswordFocus] = useState(false);
  const [isPasswordLengthValid, setIsPasswordLengthValid] = useState(false);
  const [isPasswordUppercase, setIsPasswordUppercase] = useState(false);
  const [isPasswordLowercase, setIsPasswordLowercase] = useState(false);
  const [isPasswordNumber, setIsPasswordNumber] = useState(false);
  const [isPasswordSpecialCharacter, setIsPasswordSpecialCharacter] = useState(false);
  const [passwordStrengthScore, setPasswordStrengthScore] = useState(0);

  const toggleShowPass = ()=> setShowPass(!showPass);
  const type = showPass? "text" : "password";


  const onFocus = () => {
    setIsNewPasswordFocus(true);
  }

  const handleChange = (e) => {
    setPassword(e.target.value);
    const passwordQualifications = getPasswordQualifications(e.target.value);
    const passwordMatchesEmail = e.target.value === email && e.target.value !== "";
    setIsPasswordLengthValid(passwordMatchesEmail?false:passwordQualifications.MIN_LENGTH);
    setIsPasswordUppercase(passwordMatchesEmail?false:passwordQualifications.UPPER_CASE);
    setIsPasswordLowercase(passwordMatchesEmail?false:passwordQualifications.LOWER_CASE);
    setIsPasswordNumber(passwordMatchesEmail?false:passwordQualifications.NUMBER);
    setIsPasswordSpecialCharacter(passwordMatchesEmail?false:passwordQualifications.NON_ALPHA);
    setPasswordStrengthScore(passwordMatchesEmail?1:getPasswordStrengthScore(e.target.value));
  };

  const showSuccessPassword = isNewPasswordFocus && passwordStrengthScore === 4;

  return (      
    <Modal open={isOpen} onClose={onModalClose} size='md'>
        <ModalHeader>
          Create your password
        </ModalHeader>

        <ModalBody>
        <Typography weight="regular" variant="body-base">
          Create a strong password.
        </Typography>

        <form className="sui-flex sui-mt-4 sui-mb-4 sui-items-end sui-gap-2 ">
          <div className="sui-w-4/5">
            <TextField
              id="set-password"
              label="Password"
              value={password.value}
              fullWidth
              onChange={handleChange}
              onFocus={onFocus}
              status={showSuccessPassword && "success"}
              type={type}
            />
          </div>
          <div className="sui-w-1/5">
            <Button variant="text" onClick={toggleShowPass}>
              {showPass ? "Hide":"Show"}
            </Button>
          </div>
        </form>

        <CSSTransition
              in={ isNewPasswordFocus  }
              timeout={0}
              classNames="error"
              unmountOnExit
            >

              <PasswordRequirementsOutline
                password={{value: password}}
                isPasswordLengthValid={isPasswordLengthValid}
                isPasswordUppercase={isPasswordUppercase}
                isPasswordLowercase={isPasswordLowercase}
                isPasswordNumber={isPasswordNumber}
                isPasswordSpecialCharacter={isPasswordSpecialCharacter}
                passwordStrengthScore={passwordStrengthScore}
              />
            </CSSTransition>

        </ModalBody>

        <ModalFooter>
          <ButtonGroup orientation="vertical">
        <Button type="button"  variant="primary" fullWidth disabled={!showSuccessPassword} onClick={setStartVerifyIdentityView} data-automation-id={unenrollmentSubmitButtonAutomationId}>
          Save Password
        </Button>
        <Button variant="secondary" fullWidth onClick={onModalClose} >
          Cancel
        </Button>
          </ButtonGroup>
        </ModalFooter>
      </Modal>
  )
}

function VerifyIdentity({isOpen, onModalClose, email, setVerifyCodeView}) {
  return (
    <Modal open={isOpen} onClose={onModalClose} size='md'>
      <ModalHeader>
        Verify Your Identity
      </ModalHeader>

      <ModalBody>
      <Typography weight="regular" variant="body-base">
        Before we save this password, we need to confirm your identity by sending a one-time verification code to your email address:
      </Typography>

      <div className="sui-my-4">
      <Typography weight="bold" variant="body-base">
        {email}    
      </Typography>
      </div>

      </ModalBody>

      <ModalFooter>
        <ButtonGroup orientation="vertical">
      <Button variant="primary" type="button" fullWidth onClick={setVerifyCodeView}>
        Send Code
      </Button>
      <Button variant="secondary" fullWidth onClick={onModalClose} data-automation-id={unenrollmentCancelButtonAutomationId}>
        No, cancel
      </Button>
        </ButtonGroup>
      </ModalFooter>
    </Modal>
  )
}

function VerifyCode({isOpen, onModalClose, email, verificationOption, setVerificationOption, existingPhoneNumber, otpOptions, setEncryptedPayload, encryptedPayload,  setIsSubmitting, isSubmitting, password, unenrollFromPasswordlessAPI, errorToShow, setErrorToShow}) {

  const [ OTP, setOTP] = useState("");

  const onChangeOTP = (e) => {
    const otp = e.target.value.trim().replace(/[^\d]/, "");
    setOTP(otp);
  };

  const otpOption = (verificationOption === "email") ? email : formatPhoneNumber2(existingPhoneNumber);
  const showPhoneOptions = (otpOptions.length > 1);
  const enableSubmitButton = OTP.length === 6;
  const showErrorMessage = errorToShow !== "" ? true : false


  const resendCode = (otpChannel) => {
    setIsSubmitting(true);
    getOTPv2(email, encryptedPayload, otpChannel).then(({response, error}) => {
      setIsSubmitting(false);
      if (response) {
        setEncryptedPayload(response.data?.encryptedPayload);
      } else {
        const errorMessage = handleApiError(error?.response?.data?.errorMessage, "update password with otp in sign in with password");
        setErrorToShow(errorMessage)
      }
    });
    setVerificationOption(otpChannel);
  }

  const submitUpdatePassword = () => {

    setIsSubmitting(true);
    updatePasswordWithOTP(password, email, encryptedPayload, OTP, "UnenrollmentPasswordSet").then(({response, error}) => {
      setIsSubmitting(false);
      if (response) {
        unenrollFromPasswordlessAPI();
        onModalClose();

      } else {
        const errorCode = getErrorCode(error?.response?.data?.errorCode);
        const errorMessage = handleApiError(error?.response?.data?.errorMessage, "update password with otp in sign in with password");
        if (errorCode === "AUTH_ERR_125") {
          setErrorToShow(MESSAGE.OTP_ATTEMPTS_EXHAUSTED)
        } else {
          setErrorToShow(errorMessage)
          
        }
      }
    });
  }

  return (
    <Modal open={isOpen} onClose={onModalClose} size='md'>
      <ModalHeader>
        Verify Your Identity
      </ModalHeader>

      <ModalBody>

        {isSubmitting 
          ? <OtpLoadingView /> 
          : <>
            <Typography weight="regular" variant="body-base">
              We sent a verification code to <strong>{otpOption}</strong>
            </Typography>

            <div className="sui-my-4">
              <TextField
                id="set-password"
                label="Enter Verification Code"
                value={OTP || ""}
                onChange={onChangeOTP}
                type="tel"
                fullWidth
                InputProps={{ inputAttributes: { maxLength: "6"}}}
                status={showErrorMessage && "error"}
                statusMessage={showErrorMessage && errorToShow}
              />
            </div>

            <div className="sui-my-4">
              <Typography weight="regular" variant="body-base">
                Having issues?
              </Typography>
              <Typography weight="regular" variant="body-base">
                Here are some options.
              </Typography>
            </div>

            <div className="sui-my-4 sui-flex sui-flex-col sui-items-baseline sui-gap-2">
                <button type="button" onClick={() => resendCode(RESEND_OTP_CODE_OPTIONS[verificationOption][0].value)} className="sui-font-regular sui-text-base sui-tracking-normal sui-normal-case sui-line-clamp-unset sui-text-primary sui-underline" >
                  {RESEND_OTP_CODE_OPTIONS[verificationOption][0].text}
                </button>
                {showPhoneOptions && 
                  <>
                  <button type="button" onClick={() => resendCode(RESEND_OTP_CODE_OPTIONS[verificationOption][1].value)} className="sui-font-regular sui-text-base sui-tracking-normal sui-normal-case sui-line-clamp-unset sui-text-primary sui-underline" >
                    {RESEND_OTP_CODE_OPTIONS[verificationOption][1].text} 
                  </button>
                  <button type="button" onClick={() => resendCode(RESEND_OTP_CODE_OPTIONS[verificationOption][2].value)} className="sui-font-regular sui-text-base sui-tracking-normal sui-normal-case sui-line-clamp-unset sui-text-primary sui-underline" >
                    {RESEND_OTP_CODE_OPTIONS[verificationOption][2].text}
                  </button>
                  </>
                }
            </div>
          </> }

      



      </ModalBody>

      <ModalFooter>
        <ButtonGroup orientation="vertical">
          
      <Button variant="primary" type="submit"  onClick={() => submitUpdatePassword()} fullWidth data-automation-id={unenrollmentSubmitButtonAutomationId} disabled={!enableSubmitButton}>
        Submit
      </Button>
      <Button variant="secondary" fullWidth onClick={onModalClose} data-automation-id={unenrollmentCancelButtonAutomationId}>
        No, cancel
      </Button>
        </ButtonGroup>
      </ModalFooter>
    </Modal>
  )
}

export const PasswordlessUnenrollment = ({email}) => {

  const context = useCustomerAccount();
  const { addAlert } = useCommonContext();
  const { setStale, account } = context;
  const { isNoPasswordSet = "", profile } = account;
  const { existingPhoneNumber = ""  } = profile ;

  const [ isModalOpen, setIsModalOpen ] = useState(false);
  const [ isSubmitting, setIsSubmitting ] = useState(false);
  const [ password, setPassword] = useState("");
  const [ unenrollModalState, setUnenrollModalState ] = useState("")
  const [ verificationOption, setVerificationOption ] = useState("");
  const [ encryptedPayload, setEncryptedPayload ] = useState();
  const [ otpOptions, setOtpOptions] = useState()
  const [ errorToShow, setErrorToShow ] = useState("")



  const openModal = () => {
    setIsModalOpen(true);
    analytics.postOverlayEvent("unenroll from siwp");
  }
  const closeModal = () => {
    if(isSetPasswordInSigninWithPasswordInstead()){
      setIsSubmitting(false);
      setUnenrollModalState("");
    }
    setIsModalOpen(false);
  };


  const setVerifyCodeView = () => {
    setUnenrollModalState(START_VERIFY_CODE_VIEW)
    getOTPv2(email).then(({response, error})=>{
      if(response?.data?.encryptedPayload) {
        // Successfully received encrypted payload from GET OTP request. 
        setEncryptedPayload(response.data?.encryptedPayload)
      }else if (error) {
        setErrorToShow(error?.response?.data?.errorMessage)
      }
    })
  }

  const setStartVerifyIdentityView = () => {
    setUnenrollModalState(START_VERIFY_IDENTITY_VIEW);
    setVerificationOption("email");

    getOtpOptions(email).then(({response, error})=>{
      if(response?.data?.otpOptions){
        setOtpOptions(response?.data?.otpOptions)
      }else{
        setErrorToShow(error?.response?.data?.errorMessage)
      }
    })
  }

  const unenrollFromPasswordlessAPI = () => {
    if(!thdCustomer?.isLoggedIn || thdCustomer?.isPersisted) {
      setUrl("", API_URLS.SIGNIN);
      return
    }
    unenrollFromPasswordless().then(({ response, error }) => {
      setIsSubmitting(false);
      closeModal();
      if (response) {
        if(isSetPasswordInSigninWithPasswordInstead()){
          addAlert(MESSAGE_TYPE.SUCCESS, MESSAGE.SET_PASSWORD_SIGNIN_WITH_PASSWORD_INSTEAD_SUCCESS);
        }else{
          addAlert(MESSAGE_TYPE.SUCCESS, MESSAGE.PASSWORDLESS_UNENROLL_SUCCESS);
        }
        if (isNativeApp()) {
          postDataToEventBus("NATIVE_APP.PASSWORDLESS_UNENROLLMENT", { status: "successful" });
        }
        analytics.postSettingsSubmissionEvent("security: unenrolled from siwp");
        setStale();
      } else if (error) {
        addAlert(MESSAGE_TYPE.DANGER, MESSAGE.SOMETHING_WENT_WRONG);
      }
    });
  }

  const onUnenroll = () => {
    setIsSubmitting(true);

    if(isFeatureSwitchEnabled(FEATURE_SWITCHES.ENABLE_WEB_AUTHN_UNENROLL)){
      setWebauthnNoThanksWeb(email);
    }

    if(isSetPasswordInSigninWithPasswordInstead() && isNoPasswordSet === true){
      setUnenrollModalState(START_SET_PASSWORD_IN_SIGNIN_WITH_PASSWORD_INSTEAD_VIEW);
      setIsSubmitting(false);
    }else{
      unenrollFromPasswordlessAPI();
    }

  }

  const renderView = () => {
    switch(unenrollModalState){
      case START_SET_PASSWORD_IN_SIGNIN_WITH_PASSWORD_INSTEAD_VIEW:
        return <SetPasswordInSigninWithPasswordInstead  isOpen={isModalOpen} onModalClose={closeModal} email={email} setStartVerifyIdentityView={setStartVerifyIdentityView} password={password} setPassword={setPassword}/>
      case START_VERIFY_IDENTITY_VIEW:
        return <VerifyIdentity isOpen={isModalOpen} onModalClose={closeModal} email={email} setVerifyCodeView={setVerifyCodeView} />
      case START_VERIFY_CODE_VIEW:
        return <VerifyCode  isOpen={isModalOpen} onModalClose={closeModal} email={email} verificationOption={verificationOption} setVerificationOption={setVerificationOption} existingPhoneNumber={existingPhoneNumber} otpOptions={otpOptions} setEncryptedPayload={setEncryptedPayload} encryptedPayload={encryptedPayload} setIsSubmitting={setIsSubmitting} isSubmitting={isSubmitting}  password={password} unenrollFromPasswordlessAPI={unenrollFromPasswordlessAPI} addAlert={addAlert} setErrorToShow={setErrorToShow} errorToShow={errorToShow} />
      default: 
        return <PasswordlessUnenrollmentModal isOpen={isModalOpen} onUnenroll={onUnenroll} onModalClose={closeModal}  isSubmitting={isSubmitting}/>
    } 
  }

  return (
    <div className="col__12-12 u--paddingNone u--paddingXsmall-top">
      <button type="button"
        onClick={openModal}
        data-automation-id={unenrollmentLinkAutomationId}>
        <Link className="security-page-default-link">Sign in with a password instead</Link>
      </button>
      { renderView()}
    </div>
  );
}